import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import './user-profile.scss';
import { FaUser, FaSignOutAlt, FaCog, FaCrown, FaHistory, FaComments } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../../services/subscription';
import { RealtimeChannel } from '@supabase/supabase-js';
import type { Database } from '../../lib/supabase';

type Message = Database['public']['Tables']['messages']['Row'];

interface RealtimePayload<T> {
  new: T;
  old: T;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

export function UserProfile() {
  const { user, signOut, isPremium, subscriptionStatus } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageChannel, setMessageChannel] = useState<RealtimeChannel | null>(null);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const firstName = user?.user_metadata?.firstName || 'Doctor';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const channel = subscribeToNewMessages();
      return () => {
        channel?.unsubscribe();
      };
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('messages')
      .select('id')
      .eq('receiver_id', user.id)
      .eq('read', false);

    if (error) {
      console.error('Error fetching unread count:', error);
      return;
    }

    setUnreadCount(data?.length || 0);
  };

  const subscribeToNewMessages = () => {
    if (!user) return null;

    const channel = supabase.channel('user-messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            if (!(payload.new as Message).read) {
              setUnreadCount(count => count + 1);
            }
          } else if (payload.eventType === 'UPDATE') {
            // Recalculate unread count when messages are marked as read
            const { data } = await supabase
              .from('messages')
              .select('id')
              .eq('receiver_id', user.id)
              .eq('read', false);

            setUnreadCount(data?.length || 0);
          }
        }
      )
      .subscribe();

    setMessageChannel(channel);
    return channel;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleUpgrade = async () => {
    if (!user?.email) {
      alert('Please sign in to upgrade');
      return;
    }

    try {
      const paymentUrl = await subscriptionService.initializePayment(user.email, 20000);
      window.location.href = paymentUrl;
    } catch (error) {
      console.error('Payment initialization failed:', error);
      alert('Failed to initialize payment. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (!user?.email) return;

    try {
      const success = await subscriptionService.cancelSubscription(user.email);
      if (success) {
        alert('Subscription cancelled successfully');
        window.location.reload();
      } else {
        alert('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
  };

  return (
    <div
      className="user-profile"
      ref={dropdownRef}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        className="header-profile-button"
        onClick={() => user ? setIsOpen(!isOpen) : navigate('/login')}
      >
        <div className="profile-icon">
          <FaUser size={12} />
        </div>
        {user ? (
          <span className="profile-name">Dr. {firstName}</span>
        ) : (
          <span className="profile-name">Sign In</span>
        )}
        {unreadCount > 0 && (
          <div className="notification-badge">
            {unreadCount}
          </div>
        )}
      </button>

      {isOpen && user && (
        <div className="profile-dropdown">
          <div className="dropdown-header">
            <span className="email">{user?.email}</span>
            <div className="subscription-info">
              <span className="subscription-status">
                {subscriptionStatus?.status ?? 'trial'}
                {subscriptionStatus?.daysLeft && subscriptionStatus.daysLeft > 0 && subscriptionStatus.status !== 'expired' && (
                  <small> ({subscriptionStatus.daysLeft} days left)</small>
                )}
              </span>
              {isPremium ? (
                <button
                  className="cancel-button"
                  onClick={handleCancelSubscription}
                >
                  Downgrade
                </button>
              ) : (
                <button
                  className="upgrade-button"
                  onClick={handleUpgrade}
                >
                  <FaCrown /> Upgrade
                </button>
              )}
            </div>
          </div>

          <div className="dropdown-items">

            <button
              className="dropdown-item"
              onClick={() => {
                setIsOpen(false);
                navigate('/history');
              }}
            >
              <FaHistory />
              History
            </button>
            <button
              className="dropdown-item"
              onClick={() => {
                setIsOpen(false);
                navigate('/settings');
              }}
            >
              <FaCog />
              Settings
            </button>
            <button
              className="dropdown-item"
              onClick={() => {
                setIsOpen(false);
                navigate('/chat');
              }}
            >
              <FaComments />
              Chat Us
              {unreadCount > 0 && (
                <span className="chat-badge">{unreadCount}</span>
              )}
            </button>
            <button
              className="dropdown-item sign-out"
              onClick={handleSignOut}
            >
              <FaSignOutAlt />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}